import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

export default function TelegramConfigDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [botToken, setBotToken] = useState('');
  const [teacherChatId, setTeacherChatId] = useState('');
  const [tokenDirty, setTokenDirty] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadConfig = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const r = await fetch('/api/admin/telegram-config', {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Не удалось загрузить настройки');
      const data = await r.json();
      setStatus(data);
      setBotToken('');
      setTokenDirty(false);
      setTeacherChatId(data.teacherChatId || '');
    } catch (e) {
      setFeedback({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadConfig();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const body = { teacherChatId };
      if (tokenDirty) body.botToken = botToken;
      const r = await fetch('/api/admin/telegram-config', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Не удалось сохранить');
      const data = await r.json();
      setStatus((s) => ({ ...(s || {}), ...data }));
      setTokenDirty(false);
      setBotToken('');
      setFeedback({
        type: 'success',
        text: 'Настройки сохранены.' + (tokenDirty ? ' Бот перезапущен.' : ''),
      });
    } catch (e) {
      setFeedback({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const r = await fetch('/api/admin/telegram-config/test', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || 'Ошибка теста');
      setFeedback({
        type: 'success',
        text: data.message || 'Тест прошёл успешно',
      });
    } catch (e) {
      setFeedback({ type: 'error', text: e.message });
    } finally {
      setTesting(false);
    }
  };

  const sourceChip = (src) => {
    if (src === 'db') return <Chip size="small" label="БД" color="primary" />;
    if (src === 'env')
      return <Chip size="small" label=".env" color="default" />;
    return <Chip size="small" label="не задано" color="warning" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TelegramIcon color="primary" />
        <Box component="span">Интеграция с Telegram</Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              Настройки хранятся в базе данных. Значения из .env используются
              только как запасной вариант, если в БД пусто. Изменение токена
              бота перезапускает polling автоматически.
            </Alert>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
              >
                Токен бота
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {status && sourceChip(status.source && status.source.botToken)}
                {status && status.hasToken && !tokenDirty && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {status.tokenMasked}
                  </Typography>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                placeholder={
                  status && status.hasToken
                    ? 'Оставьте пустым, чтобы не менять'
                    : '123456789:AA...'
                }
                type={showToken ? 'text' : 'password'}
                value={botToken}
                onChange={(e) => {
                  setBotToken(e.target.value);
                  setTokenDirty(true);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowToken((v) => !v)}
                        edge="end"
                      >
                        {showToken ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Получите у @BotFather в Telegram.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
              >
                Chat ID преподавателей
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {status && sourceChip(status.source && status.source.chatId)}
              </Box>
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                placeholder="-1001234567890"
                value={teacherChatId}
                onChange={(e) => setTeacherChatId(e.target.value)}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Числовой ID групповго чата, куда бот отправляет домашние задания
                и отчёты.
              </Typography>
            </Box>

            {feedback && (
              <Alert severity={feedback.type}>{feedback.text}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleTest}
          disabled={testing || loading}
          startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}
        >
          Тестовое сообщение
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Закрыть
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
